import React, { useState, useEffect } from 'react';
import './Home.css';
import './ProductsActions.css';
import DateFilter from '../DateFilter';
import apiService from '../../services/api';
import PopupModal from '../PopupModal';
import AddPaymentModal from './AddPaymentModal';
import ViewSaleModal from './ViewSaleModal';
import { 
  Banknote, Search, Eye, TrendingUp, DollarSign, Activity, FileText, 
  RotateCcw, ShoppingBag, Clock, Receipt, CreditCard, Sparkles, 
  MapPin, Calendar, AlertTriangle, ArrowUpRight, CheckCircle2, ShieldAlert
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';

const Home = ({ currentUser }) => {
  const { formatCurrency, formatCurrencyNumber, formatDate, quantityPrecision } = useBusinessSettings();
  const [selectedLocationId, setSelectedLocationId] = useState('all');

  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date()
  });

  const [totalsData, setTotalsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesChartData, setSalesChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  const [fyChartData, setFyChartData] = useState([]);
  const [fyChartLoading, setFyChartLoading] = useState(true);
  const [salesDues, setSalesDues] = useState([]);
  const [purchaseDues, setPurchaseDues] = useState([]);
  const [duesLoading, setDuesLoading] = useState(true);

  const [salesDuesLocation, setSalesDuesLocation] = useState('all');
  const [purchaseDuesLocation, setPurchaseDuesLocation] = useState('all');
  const [chequePaymentsLocation, setChequePaymentsLocation] = useState('all');

  const [salesPage, setSalesPage] = useState(1);
  const [purchasePage, setPurchasePage] = useState(1);
  const [stockPage, setStockPage] = useState(1);
  const itemsPerPage = 10;

  const [stockAlerts, setStockAlerts] = useState([]);
  const [stockAlertLoading, setStockAlertLoading] = useState(true);
  const [stockAlertLocation, setStockAlertLocation] = useState('all');

  const [salesSearch, setSalesSearch] = useState('');
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [chequePaymentsSearch, setChequePaymentsSearch] = useState('');

  const [chequePayments, setChequePayments] = useState([]);
  const [chequePaymentsLoading, setChequePaymentsLoading] = useState(true);
  const [chequePaymentsPage, setChequePaymentsPage] = useState(1);
  const [editingChequeId, setEditingChequeId] = useState(null);
  const [viewSaleModal, setViewSaleModal] = useState({ open: false, sale: null, loading: false });

  const [paymentModal, setPaymentModal] = useState({ open: false, type: null, row: null });

  const [openActionId, setOpenActionId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const toggleActionMenu = (id) => {
    setOpenActionId(openActionId === id ? null : id);
  };

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiService.getLocations();
        setLocations(response.data || response || []);
      } catch (error) {
      /* console.error('Error fetching locations:', error); */
      }
    };
    fetchLocations();
  }, []);

  // Fetch sales last 30 days chart data
  useEffect(() => {
    const fetchSalesChart = async () => {
      if (!apiService.isTokenValid()) return;
      try {
        setChartLoading(true);
        const response = await apiService.getSalesLast30Days();
        const data = response?.data || response || {};
        const labels = data.labels || [];
        const totals = data.total_sales || [];
        const mapped = labels.map((label, i) => ({
          name: label,
          sales: parseFloat(totals[i] || 0)
        }));
        setSalesChartData(mapped);
      } catch (error) {
      /* console.error('Error fetching sales chart:', error); */
        setSalesChartData([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchSalesChart();
  }, []);

  // Fetch financial-year sales chart data
  useEffect(() => {
    const fetchFyChart = async () => {
      if (!apiService.isTokenValid()) return;
      try {
        setFyChartLoading(true);
        const res = await apiService.getSalesCurrentFinancialYear({
          location_id: selectedLocationId === 'all' ? null : selectedLocationId
        });
        const payload = res?.data?.data || res?.data || {};
        const labels = payload.labels || [];
        const values = payload.values || [];
        const mapped = labels.map((label, i) => ({
          name: label,
          sales: parseFloat(values[i] || 0)
        }));
        setFyChartData(mapped);
      } catch (error) {
      /* console.error('Error fetching FY sales chart:', error); */
        setFyChartData([]);
      } finally {
        setFyChartLoading(false);
      }
    };
    fetchFyChart();
  }, [selectedLocationId]);

  // Fetch payment dues tables
  const fetchDuesTables = async (salesLoc = salesDuesLocation, purchaseLoc = purchaseDuesLocation) => {
    if (!apiService.isTokenValid()) return;
    try {
      setDuesLoading(true);
      const [salesRes, purchaseRes] = await Promise.all([
        apiService.getSalesPaymentDues({ location_id: salesLoc === 'all' ? null : salesLoc }),
        apiService.getPurchasePaymentDues({ location_id: purchaseLoc === 'all' ? null : purchaseLoc })
      ]);
      setSalesDues(salesRes?.data || []);
      setPurchaseDues(purchaseRes?.data || []);
    } catch (error) {
      /* console.error('Error fetching dues tables:', error); */
      setSalesDues([]);
      setPurchaseDues([]);
    } finally {
      setDuesLoading(false);
    }
  };

  // Fetch cheque payments table
  const fetchChequePayments = async (location = chequePaymentsLocation) => {
    if (!apiService.isTokenValid()) return;
    try {
      setChequePaymentsLoading(true);
      const res = await apiService.getChequePayments({ location_id: location === 'all' ? null : location });
      setChequePayments(res?.data || []);
    } catch (error) {
      /* console.error('Error fetching cheque payments:', error); */
      setChequePayments([]);
    } finally {
      setChequePaymentsLoading(false);
    }
  };

  useEffect(() => {
    fetchDuesTables();
  }, [salesDuesLocation, purchaseDuesLocation]);

  useEffect(() => {
    fetchChequePayments();
  }, [chequePaymentsLocation]);

  useEffect(() => { setSalesPage(1); }, [salesDuesLocation, salesSearch]);
  useEffect(() => { setPurchasePage(1); }, [purchaseDuesLocation, purchaseSearch]);
  useEffect(() => { setStockPage(1); }, [stockAlertLocation, stockSearch]);
  useEffect(() => { setChequePaymentsPage(1); }, [chequePaymentsLocation, chequePaymentsSearch]);

  // Fetch dashboard totals
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!apiService.isTokenValid()) return;

      try {
        setLoading(true);

        const formatForApi = (date) => {
          if (!date) return '';
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const params = {
          start: formatForApi(dateRange.start),
          end: formatForApi(dateRange.end),
          location_id: selectedLocationId === 'all' ? null : selectedLocationId
        };

        const data = await apiService.getDashboardTotals(params);
        setTotalsData(data);
      } catch (error) {
      /* console.error('❌ DASHBOARD DEBUG - Totals Error:', error); */
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [dateRange, selectedLocationId]);

  const filterRows = (rows, term) => {
    if (!term) return rows;
    const lower = term.toLowerCase();
    return rows.filter(row =>
      String(row.contact || row.customer || row.product || '').toLowerCase().includes(lower) ||
      String(row.reference_no || row.invoice_no || row.cheque_number || row.location || '').toLowerCase().includes(lower)
    );
  };

  const filteredSales = filterRows(salesDues, salesSearch);
  const salesTotalPages = Math.ceil(filteredSales.length / itemsPerPage) || 1;
  const paginatedSales = filteredSales.slice((salesPage - 1) * itemsPerPage, salesPage * itemsPerPage);

  const filteredPurchase = filterRows(purchaseDues, purchaseSearch);
  const purchaseTotalPages = Math.ceil(filteredPurchase.length / itemsPerPage) || 1;
  const paginatedPurchase = filteredPurchase.slice((purchasePage - 1) * itemsPerPage, purchasePage * itemsPerPage);

  const filteredStock = filterRows(stockAlerts, stockSearch);
  const stockTotalPages = Math.ceil(filteredStock.length / itemsPerPage) || 1;
  const paginatedStock = filteredStock.slice((stockPage - 1) * itemsPerPage, stockPage * itemsPerPage);

  const filteredChequePayments = filterRows(chequePayments, chequePaymentsSearch);
  const chequePaymentsTotalPages = Math.ceil(filteredChequePayments.length / itemsPerPage) || 1;
  const paginatedChequePayments = filteredChequePayments.slice((chequePaymentsPage - 1) * itemsPerPage, chequePaymentsPage * itemsPerPage);

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }

    return (
      <div className="etpos-pagination">
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="pg-btn pg-prev"
        >
          Previous
        </button>
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={i} className="pg-ellipsis">...</span>
          ) : (
            <button 
              key={i} 
              onClick={() => onPageChange(p)} 
              className={`pg-btn ${p === currentPage ? 'pg-active' : ''}`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="pg-btn pg-next"
        >
          Next
        </button>
      </div>
    );
  };

  const getSalesMetrics = () => {
    const d = totalsData?.data || totalsData || {};

    const spinner = <svg className="circular-arrow-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>;

    return [
      { 
        title: 'Total Sales', 
        value: loading ? spinner : formatCurrency(d.total_sell || 0), 
        icon: <DollarSign size={22} />, 
        color: 'blue',
        badge: 'Sales',
        gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
      },
      { 
        title: 'Net', 
        value: loading ? spinner : formatCurrency(d.net || 0), 
        icon: <Activity size={22} />, 
        color: 'green',
        badge: 'Revenue',
        gradient: 'linear-gradient(135deg, #10b981, #047857)'
      },
      { 
        title: 'Invoice due', 
        value: loading ? spinner : formatCurrency(d.invoice_due || 0), 
        icon: <FileText size={22} />, 
        color: 'orange',
        badge: 'Receivables',
        gradient: 'linear-gradient(135deg, #f59e0b, #b45309)'
      },
      { 
        title: 'Total Sell Return', 
        value: loading ? spinner : formatCurrency(d.total_sell_return || 0), 
        icon: <RotateCcw size={22} />, 
        color: 'red',
        badge: 'Returns',
        gradient: 'linear-gradient(135deg, #ef4444, #b91c1c)'
      },
      { 
        title: 'Total purchase', 
        value: loading ? spinner : formatCurrency(d.total_purchase || 0), 
        icon: <ShoppingBag size={22} />, 
        color: 'purple',
        badge: 'Purchases',
        gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)'
      },
      { 
        title: 'Purchase due', 
        value: loading ? spinner : formatCurrency(d.purchase_due || 0), 
        icon: <Clock size={22} />, 
        color: 'yellow',
        badge: 'Payables',
        gradient: 'linear-gradient(135deg, #eab308, #a16207)'
      },
      { 
        title: 'Total Purchase Return', 
        value: loading ? spinner : formatCurrency(d.total_purchase_return || 0), 
        icon: <Receipt size={22} />, 
        color: 'pink',
        badge: 'P-Returns',
        gradient: 'linear-gradient(135deg, #ec4899, #be185d)'
      },
      { 
        title: 'Expense', 
        value: loading ? spinner : formatCurrency(d.total_expense || 0), 
        icon: <CreditCard size={22} />, 
        color: 'gray',
        badge: 'Outflow',
        gradient: 'linear-gradient(135deg, #64748b, #334155)'
      },
    ];
  };

  // Fetch product stock alerts
  const fetchStockAlerts = async () => {
    if (!apiService.isTokenValid()) return;
    try {
      setStockAlertLoading(true);
      const response = await apiService.getProductStockAlert({
        location_id: stockAlertLocation === 'all' ? null : stockAlertLocation
      });
      setStockAlerts(response?.data || []);
    } catch (error) {
      /* console.error('Error fetching stock alerts:', error); */
      setStockAlerts([]);
    } finally {
      setStockAlertLoading(false);
    }
  };

  useEffect(() => {
    fetchStockAlerts();
  }, [stockAlertLocation]);

  return (
    <div className="etpos-home">
      {/* Modern Hero Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-banner-content">
          <div className="welcome-user-info">
            <div className="welcome-avatar-ring">
              <span className="welcome-avatar-icon">👋</span>
            </div>
            <div>
              <h2 className="welcome-heading">
                Welcome back, <span className="user-name-highlight">{currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username : 'User'}</span>
              </h2>
              <p className="welcome-subtext">Real-time breakdown of your sales, purchases, dues, and inventory alerts.</p>
            </div>
          </div>

          <div className="controls-row">
            <div className="location-selector">
              <label><MapPin size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Select location</label>
              <select value={selectedLocationId} onChange={(e) => setSelectedLocationId(e.target.value)}>
                <option value="all">📍 All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>📍 {loc.name}</option>
                ))}
              </select>
            </div>
            <div className="location-selector">
              <label><Calendar size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Select date range</label>
              <DateFilter 
                onDateChange={(range) => setDateRange({ start: range.start, end: range.end })}
                initialStartDate={dateRange.start}
                initialEndDate={dateRange.end}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="metrics-grid">
        {getSalesMetrics().map((metric, index) => (
          <div key={index} className={`metric-card metric-${metric.color}`}>
            <div className="metric-icon-box" style={{ background: metric.gradient }}>
              {metric.icon}
            </div>
            <div className="metric-content">
              <div className="metric-header-row">
                <span className="metric-title">{metric.title}</span>
                {metric.badge && <span className="metric-tag">{metric.badge}</span>}
              </div>
              <h3 className="metric-value">{metric.value}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="charts-row">
          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-header-title">
                <div className="chart-title-icon chart-icon-blue">
                  <TrendingUp size={18} />
                </div>
                <div>
                  <h3>Sales Last 30 Days</h3>
                  <p className="chart-subtitle">Daily sales velocity & performance graph</p>
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ height: '300px', marginTop: '1rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesChartData.length > 0 ? salesChartData : [{ name: 'No data', sales: 0 }]} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.45}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(226, 232, 240, 0.7)" />
                  <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(val) => `$${val}`} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', background: '#ffffff' }} />
                  <Area type="monotone" dataKey="sales" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-header-title">
                <div className="chart-title-icon chart-icon-green">
                  <Activity size={18} />
                </div>
                <div>
                  <h3>Sales Current Financial Year</h3>
                  <p className="chart-subtitle">Monthly total revenue breakdown</p>
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ height: '300px', marginTop: '1rem' }}>
              {fyChartLoading ? (
                <div className="chart-loading">
                  <div className="spinner-dot"></div>
                  <span>Loading performance chart...</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fyChartData.length ? fyChartData : [{ name: 'No data', sales: 0 }]} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorFySales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="#059669" stopOpacity={0.6}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(226, 232, 240, 0.7)" />
                    <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', background: '#ffffff' }} />
                    <Bar dataKey="sales" fill="url(#colorFySales)" radius={[8, 8, 0, 0]} barSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className="tables-section">
        {/* Sales Payment Due Table */}
        <div className="table-card">
          <div className="table-header">
            <div className="table-title-group">
              <div className="table-badge-icon icon-orange"><FileText size={16} /></div>
              <h3>Sales Payment Due</h3>
            </div>
            <div className="table-controls">
              <div className="search-box-custom">
                <Search size={13} color="#64748b" />
                <input
                  type="text"
                  placeholder="Search customer / invoice..."
                  value={salesSearch}
                  onChange={(e) => setSalesSearch(e.target.value)}
                />
              </div>
              <div className="location-select-wrap">
                <select value={salesDuesLocation} onChange={(e) => setSalesDuesLocation(e.target.value)}>
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Customer</th>
                  <th style={{ width: '25%' }}>Invoice No</th>
                  <th style={{ width: '20%' }}>Due Amount</th>
                  <th style={{ width: '20%' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {duesLoading ? (
                  <tr><td colSpan="4" className="no-data">Loading dues...</td></tr>
                ) : paginatedSales.length === 0 ? (
                  <tr><td colSpan="4" className="no-data">No sales payment dues found</td></tr>
                ) : (
                  paginatedSales.map((row, idx) => (
                    <tr key={idx}>
                      <td className="cell-truncate">{row.contact}</td>
                      <td className="cell-bold-code">{row.reference_no}</td>
                      <td className="cell-highlight-due">{formatCurrency(row.due_amount)}</td>
                      <td>
                        <button
                          className="btn-action-payment"
                          onClick={() => setPaymentModal({ open: true, type: 'sell', row })}
                        >
                          <Banknote size={13} /> Add payment
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredSales.length > 0 && (
            <div className="table-footer">
              <span className="footer-entries-info">
                Showing {(salesPage - 1) * itemsPerPage + 1} to {Math.min(salesPage * itemsPerPage, filteredSales.length)} of {filteredSales.length} entries
              </span>
              <Pagination currentPage={salesPage} totalPages={salesTotalPages} onPageChange={setSalesPage} />
            </div>
          )}
        </div>

        {/* Purchase Payment Due Table */}
        <div className="table-card">
          <div className="table-header">
            <div className="table-title-group">
              <div className="table-badge-icon icon-purple"><ShoppingBag size={16} /></div>
              <h3>Purchase Payment Due</h3>
            </div>
            <div className="table-controls">
              <div className="search-box-custom">
                <Search size={13} color="#64748b" />
                <input
                  type="text"
                  placeholder="Search supplier / ref..."
                  value={purchaseSearch}
                  onChange={(e) => setPurchaseSearch(e.target.value)}
                />
              </div>
              <div className="location-select-wrap">
                <select value={purchaseDuesLocation} onChange={(e) => setPurchaseDuesLocation(e.target.value)}>
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Supplier</th>
                  <th style={{ width: '25%' }}>Reference No</th>
                  <th style={{ width: '20%' }}>Due Amount</th>
                  <th style={{ width: '20%' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {duesLoading ? (
                  <tr><td colSpan="4" className="no-data">Loading dues...</td></tr>
                ) : paginatedPurchase.length === 0 ? (
                  <tr><td colSpan="4" className="no-data">No purchase payment dues found</td></tr>
                ) : (
                  paginatedPurchase.map((row, idx) => (
                    <tr key={idx}>
                      <td className="cell-truncate">{row.contact}</td>
                      <td className="cell-bold-code">{row.reference_no}</td>
                      <td className="cell-highlight-due">{formatCurrency(row.due_amount)}</td>
                      <td>
                        <button
                          className="btn-action-payment"
                          onClick={() => setPaymentModal({ open: true, type: 'purchase', row })}
                        >
                          <Banknote size={13} /> Add payment
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredPurchase.length > 0 && (
            <div className="table-footer">
              <span className="footer-entries-info">
                Showing {(purchasePage - 1) * itemsPerPage + 1} to {Math.min(purchasePage * itemsPerPage, filteredPurchase.length)} of {filteredPurchase.length} entries
              </span>
              <Pagination currentPage={purchasePage} totalPages={purchaseTotalPages} onPageChange={setPurchasePage} />
            </div>
          )}
        </div>
      </div>

      {/* Check Payments Table */}
      <div className="table-card">
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-blue"><CheckCircle2 size={16} /></div>
            <h3>Check Payment Dues</h3>
          </div>
          <div className="table-controls">
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search check / customer..."
                value={chequePaymentsSearch}
                onChange={(e) => setChequePaymentsSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={chequePaymentsLocation} onChange={(e) => setChequePaymentsLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Customer</th>
                <th style={{ width: '15%' }}>Invoice No</th>
                <th style={{ width: '15%' }}>Check Number</th>
                <th style={{ width: '12%' }}>Expiry</th>
                <th style={{ width: '15%' }}>Submission</th>
                <th style={{ width: '13%' }}>Status</th>
                <th style={{ width: '10%' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {chequePaymentsLoading ? (
                <tr><td colSpan="7" className="no-data">Loading check details...</td></tr>
              ) : paginatedChequePayments.length === 0 ? (
                <tr><td colSpan="7" className="no-data">No check payment dues found</td></tr>
              ) : (
                paginatedChequePayments.map((p, i) => (
                  <tr key={p.id || i}>
                    <td className="cell-truncate">{p.customer}</td>
                    <td className="cell-bold-code">{p.invoice_no}</td>
                    <td className="cell-bold-code">{p.cheque_number}</td>
                    <td>{formatDate(p.cheque_expiry_date)}</td>
                    <td>{formatDate(p.bank_submission_date)}</td>
                    <td>
                      <div className="status-cell-wrapper">
                        <select
                          className={`status-select-pill ${p.status === 'Completed' ? 'status-completed' : 'status-pending'}`}
                          value={p.status || 'Pending'}
                          disabled={editingChequeId !== p.id}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await apiService.updateChequeStatus(p.id, newStatus);
                              setChequePayments(prev => prev.map(cp => cp.id === p.id ? { ...cp, status: newStatus } : cp));
                              setEditingChequeId(null);
                            } catch (err) {
      /* console.error('Failed to update cheque status:', err); */
                            }
                          }}
                        >
                          <option value="Pending">🟡 Pending</option>
                          <option value="Completed">🟢 Completed</option>
                        </select>
                        <button
                          onClick={() => setEditingChequeId(editingChequeId === p.id ? null : p.id)}
                          className="btn-edit-status"
                          title="Toggle edit status"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn-action-view"
                        onClick={async () => {
                          if (!p.transaction_id) return;
                          try {
                            const saleData = await apiService.getSaleView(p.transaction_id);
                            let extractedSale = saleData;
                            if (saleData && saleData.data) {
                              extractedSale = Array.isArray(saleData.data) ? saleData.data[0] : saleData.data;
                            } else if (Array.isArray(saleData)) {
                              extractedSale = saleData[0];
                            }
                            setViewSaleModal({ open: true, sale: { apiData: extractedSale }, loading: false });
                          } catch (err) {
      /* console.error('Failed to fetch sale details:', err); */
                            alert('Failed to load sale details');
                          }
                        }}
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredChequePayments.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(chequePaymentsPage - 1) * itemsPerPage + 1} to {Math.min(chequePaymentsPage * itemsPerPage, filteredChequePayments.length)} of {filteredChequePayments.length} entries
            </span>
            <Pagination currentPage={chequePaymentsPage} totalPages={chequePaymentsTotalPages} onPageChange={setChequePaymentsPage} />
          </div>
        )}
      </div>

      {/* Product Stock Alert */}
      <div className="table-card">
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-red"><AlertTriangle size={16} /></div>
            <h3>Product Stock Alert</h3>
          </div>
          <div className="table-controls">
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search product..."
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={stockAlertLocation} onChange={(e) => setStockAlertLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Location</th>
                <th>Current stock</th>
              </tr>
            </thead>
            <tbody>
              {stockAlertLoading ? (
                <tr><td colSpan="3" className="no-data">Loading stock alerts...</td></tr>
              ) : paginatedStock.length === 0 ? (
                <tr><td colSpan="3" className="no-data">No stock alerts found</td></tr>
              ) : (
                paginatedStock.map((alert, i) => (
                  <tr key={i}>
                    <td className="product-name">{alert.product}</td>
                    <td>{alert.location}</td>
                    <td>
                      <span className="stock-alert-pill">
                        {parseFloat(alert.stock || 0).toFixed(quantityPrecision)} {alert.unit}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredStock.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(stockPage - 1) * itemsPerPage + 1} to {Math.min(stockPage * itemsPerPage, filteredStock.length)} of {filteredStock.length} entries
            </span>
            <Pagination currentPage={stockPage} totalPages={stockTotalPages} onPageChange={setStockPage} />
          </div>
        )}
      </div>

      {/* View Sale Modal */}
      {viewSaleModal.open && (
        <ViewSaleModal
          sale={viewSaleModal.sale}
          onClose={() => setViewSaleModal({ open: false, sale: null, loading: false })}
          onPackingSlip={() => {}}
          onPrintInvoice={() => {}}
        />
      )}

      {/* Add Payment Modal */}
      {paymentModal.open && paymentModal.row && (
        <AddPaymentModal
          sale={{
            id: paymentModal.row.id,
            invoice_no: paymentModal.type === 'sell' ? paymentModal.row.reference_no : undefined,
            ref_no: paymentModal.type === 'purchase' ? paymentModal.row.reference_no : undefined,
            final_total: paymentModal.row.final_total || paymentModal.row.due_amount,
            payment_due: paymentModal.row.due_amount,
            contact: { name: paymentModal.row.contact },
          }}
          invoice={paymentModal.row}
          onClose={() => setPaymentModal({ open: false, type: null, row: null })}
          onSuccess={() => {
            setPaymentModal({ open: false, type: null, row: null });
            fetchDuesTables();
          }}
        />
      )}
    </div>
  );
};

export default Home;
